<template>
  <a-modal
    v-model:open="localVisible"
    title="告警信息列表"
    width="1100px"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="alarm-modal-content">
      <!-- 告警列表表格 -->
      <a-table 
        :columns="columns" 
        :row-key="(record) => record.id" 
        :data-source="dataSource" 
        size="small" 
        class="mt15" 
        :pagination="pagination" 
        :scroll="{ y: '300px' }"
        :headerCellStyle="{ textAlign: 'center' }"
      >
        <template #bodyCell="{ column, record, index }">
          <template v-if="column.key === 'index'">
            {{ (pagination.current - 1) * pagination.pageSize + index + 1 }}
          </template>
          <template v-if="column.key === 'licenceNo'">
            {{ record.licenceNo || '-' }}
          </template>
          <template v-if="column.key === 'status'">
            <span :class="getStatusClass(record.status)">{{ getStatusText(record.status) }}</span>
          </template>
          <template v-if="column.key === 'alarmType'">
            {{ getAlarmType(record.alarmType) }}
          </template>
          <template v-if="column.key === 'alarmLevel'">
            <span :class="getAlarmLevelClass(record.alarmLevel)">{{ getAlarmLevelText(record.alarmLevel) }}</span>
          </template>
          <template v-if="column.key === 'alarmTime'">
            {{ record.alarmTime || '-' }}
          </template>
          <template v-if="column.key === 'action'">
            <span>
              <a class="ml5" @click="viewDetail(record)">查看详情</a>
              <a class="ml5" @click="handleAlarm(record)">处理</a>
            </span>
          </template>
        </template>
      </a-table>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, reactive, watch } from "vue";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  alarmType: {
    type: String,
    default: '' // 1-故障, 2-异常, 3-警告
  }
});

const emit = defineEmits(['update:visible']);

// 本地可见性状态
const localVisible = ref(props.visible);

// 监听 props.visible 变化
watch(() => props.visible, (newVal) => {
  localVisible.value = newVal;
});

// 监听本地 visible 变化，同步到父组件
watch(localVisible, (newVal) => {
  emit('update:visible', newVal);
});

const columns = [

  {
    title: "告警时间",
    key: "alarmTime",
    width: 150,
    align: "center"
  },
  {
    title: "测试/运营牌照",
    dataIndex: "licenceNo",
    key: "licenceNo",
    width: 120,
    align: "center"
  },
  {
    title: "车辆识别码",
    dataIndex: "vehicleVin",
    key: "vehicleVin",
    width: 180,
    align: "center"
  },
  {
    title: "状态",
    key: "status",
    width: 80,
    align: "center"
  },
  {
    title: "紧急程度",
    key: "alarmLevel",
    width: 80,
    align: "center"
  },
  {
    title: "告警信息",
    dataIndex: "alarmInfo",
    key: "alarmInfo",
    align: "center"
  },
  {
    title: "操作",
    key: "action",
    width: 120,
    align: "center"
  }
];

let dataSource = ref([]);

const pageChange = (page, pageSize) => {
  pagination.current = page;
  getData();
}

const pageSizeChange = (current, size) => {
  pagination.pageSize = size;
  getData();
}

const pagination = reactive({
  total: 0,
  size: "middle",
  defaultPageSize: 10,
  current: 1,
  pageSize: 10,
  showSizeChanger: true,
  showQuickJumper: false,
  pageSizeOptions: ["10", "20", "30"],
  showTotal: (total) => `共有 ${total} 条数据`,
  onShowSizeChange: (current, pageSize) => (pagination.pageSize = pageSize),
  onChange: pageChange,
  showSizeChange: pageSizeChange,
});

// 监听告警类型变化
watch(() => props.alarmType, (newType) => {
  if (newType) {
    pagination.current = 1;
    getData();
  }
});

// 监听弹窗显示
watch(() => props.visible, (newVal) => {
  if (newVal) {
    getData();
  }
});

const getStatusClass = (status) => {
  const statusMap = {
    '0': 'status-pending',
    '1': 'status-approved',
    '2': 'status-reviewing'
  };
  return statusMap[status] || 'status-default';
};

const getStatusText = (status) => {
  const statusMap = {
    '0': '未处理',
    '1': '已处理',
    '2': '处理中'
  };
  return statusMap[status] || '未知';
};

const getAlarmType = (type) => {
  const typeMap = {
    '1': '故障',
    '2': '异常',
    '3': '警告'
  };
  return typeMap[type] || '未知';
};

const getAlarmLevelClass = (level) => {
  const levelMap = {
    '1': 'alarm-high',
    '2': 'alarm-medium',
    '3': 'alarm-low'
  };
  return levelMap[level] || 'alarm-default';
};

const getAlarmLevelText = (level) => {
  const levelMap = {
    '1': '高',
    '2': '中',
    '3': '低'
  };
  return levelMap[level] || '未知';
};

const viewDetail = (record) => {
  console.log('查看详情:', record);
};

const handleAlarm = (record) => {
  console.log('处理告警:', record);
};

const getData = async () => {
  // 移除模拟数据，等待后端接口
  dataSource.value = [];
  pagination.total = 0;
};

const handleCancel = () => {
  emit('update:visible', false);
};
</script>

<style lang="less" scoped>
.alarm-modal-content {
  padding: 10px;
}

.inp {
  width: 120px;
}

/* 表头样式 */
:deep(.ant-table-thead > tr > th) {
  padding: 4px 8px !important;
  height: 24px !important;
  line-height: 1.2 !important;
  font-size: 12px !important;
}

/* 状态样式 */
.status-pending {
  color: #FA8C16;
  font-weight: 500;
}

.status-reviewing {
  color: #1E8EF2;
  font-weight: 500;
}

.status-approved {
  color: #52C41A;
  font-weight: 500;
}

.status-default {
  color: #68767E;
  font-weight: 500;
}

/* 紧急程度样式 */
.alarm-high {
  color: #F5222D;
  font-weight: 500;
}

.alarm-medium {
  color: #FA8C16;
  font-weight: 500;
}

.alarm-low {
  color: #52C41A;
  font-weight: 500;
}

.alarm-default {
  color: #68767E;
  font-weight: 500;
}

:deep(.ant-table-wrapper .ant-table-thead>tr>th),
:deep(.ant-table-wrapper .ant-table-thead>tr>td) {
  font-size: 16px;
  font-family: PingFang SC-Regular, PingFang SC;
  font-weight: 600;
  color: #0A212E;
  background-color: #EFEFF5;
  padding: 10px 12px !important;
  height: 40px !important;
  line-height: 1.5 !important;
}

:deep(.ant-table-wrapper .ant-table:not(.ant-table-bordered) .ant-table-tbody>tr) {
  color: #68767E;
}

a {
  color: #1E8EF2;
}
</style>